import { useEffect, useState } from 'react';
import ShopNavBar from '../../components/ShopNavBar'
import { useLocation, useNavigate } from 'react-router-dom';
import ItemListJob from '../../components/shop/ItemListJob';
import EmptyViewJobs from '../../components/shop/EmptyViewJobs';
import { useAuth } from '../../contexts/AuthContext';
import { jobRequestsDbRef } from '../../utils/dbRefs';
import { equalTo, onValue, orderByChild, query } from 'firebase/database';
import { getTimeLimit } from '../../utils/utils';

const ShopPastJobs = () => {
    const { currentUser } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("all");
    const [timeRange, setTimeRange] = useState("all_time");
    const navigate = useNavigate();
    const location = useLocation();


    useEffect(() => {
        if (!currentUser?.uid) return;

        setLoading(true);

        const shopJobsQuery = query(
            jobRequestsDbRef,
            orderByChild("shop/uid"),
            equalTo(currentUser.uid)
        );

        const unsubscribe = onValue(shopJobsQuery, (snapshot) => {
            const data = snapshot.val();

            console.log("Fetched jobs data:", data); // Debug log

            if (data) {
                const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

                const formatted = Object.entries(data)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    // 🟢 only completed jobs
                    .filter((job) => job.status === "Completed")
                    // 🟢 last 30 days filter
                    .filter((job) => {
                        if (!job.createdAt) return false;
                        return job.createdAt >= thirtyDaysAgo;
                    })
                    // newest first
                    .sort((a, b) => b.createdAt - a.createdAt);

                setJobs(formatted);
            } else {
                setJobs([]);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser?.uid]);


    useEffect(() => {
        let result = [...jobs];

        // 🏷 1. STATUS FILTER
        if (status !== "all") {
            if (status === "completed") {
                // show only completed jobs (all time)
                result = result.filter((job) => job.status === "Completed");
            }

            if (status === "new") {
                const limit = Date.now() - 3 * 24 * 60 * 60 * 1000;

                result = result.filter((job) => {
                    return (
                        job.status === "Completed" &&
                        (job.createdAt || 0) >= limit
                    );
                });
            }
        }

        // ⏰ 2. TIME RANGE FILTER
        if (timeRange !== "all_time") {
            const limit = getTimeLimit(timeRange);

            result = result.filter((job) => {
                const createdAt = job.createdAt || 0;
                return createdAt >= limit;
            });
        }

        setFilteredJobs(result);
    }, [jobs, status, timeRange]);

    const handleViewJob = (job) => {
        navigate('/shop/job-details', { state: { job } });
    };


    return (
        <div className='bg-background min-h-full flex flex-col font-["Plus_Jakarta_Sans",sans-serif]'>
            <ShopNavBar type={currentUser?.type || "shop"} />
            <main className='flex-1 max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:py-[32px] pb-[80px] md:pb-[40px]'>
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        Past Jobs
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        Review previously completed print jobs.
                    </p>
                </div>
                <div className="flex items-center justify-between mb-[20px] flex-wrap gap-[12px]">
                    <p className='font-semibold text-[14px] text-[#717182]'>{jobs.length} completed job{jobs.length !== 1 ? 's' : ''} - removed after 30 days</p>
                    <div
                        className='flex items-center space-x-2'
                    >
                        <div className="flex items-center gap-[8px] flex-wrap">
                            <div className="relative">
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="appearance-none bg-white border border-[#e4e5e7] rounded-[10px] px-[14px] py-[9px] pr-[34px] text-[13px] font-medium text-[#212326] cursor-pointer
                                 hover:border-[#c0c2c6] focus:outline-none focus:border-[#ff5323] transition-colors"
                                >
                                    <option value="all">All Status</option>
                                    <option value="new">New</option>
                                    <option value="completed">Completed</option>
                                </select>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                    class="lucide lucide-chevron-down absolute right-[10px] top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[#9298a0] pointer-events-none">
                                    <path d="m6 9 6 6 6-6"></path>
                                </svg>
                            </div>
                            <div className="relative">
                                <select
                                    value={timeRange}
                                    onChange={(e) => setTimeRange(e.target.value)}
                                    className="appearance-none bg-white border border-[#e4e5e7] rounded-[10px] px-[14px] py-[9px] pr-[34px] text-[13px] font-medium text-[#212326] 
                                            cursor-pointer hover:border-[#c0c2c6] focus:outline-none focus:border-[#ff5323] transition-colors">
                                    <option value="today">Today</option>
                                    <option value="this_week">This week</option>
                                    <option value="this_month">This month</option>
                                    <option value="all_time">All time</option>
                                </select>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                    class="lucide lucide-chevron-down absolute right-[10px] top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[#9298a0] pointer-events-none">
                                    <path d="m6 9 6 6 6-6"></path>
                                </svg>
                            </div>
                        </div>

                    </div>
                </div>


                {loading ? (
                    // Display loading spinner/message while fetching jobs
                    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                        <div className="bg-white p-4 rounded-md shadow-lg">
                            <p className="text-lg font-semibold">Loading...</p>
                        </div>
                    </div>
                ) :
                    filteredJobs.length > 0 ? (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[12px]'>
                            {
                                filteredJobs.map((job, index) => (
                                    <ItemListJob
                                        job={job}
                                        index={index}
                                        handleViewJob={handleViewJob}
                                        isPastJob={true}
                                    />
                                ))}
                        </div>
                    ) : (
                        <EmptyViewJobs
                            title="No completed jobs yet"
                            description="Jobs will appear here once they're marked as completed."
                        />
                    )}

            </main>
        </div>

    )
}

export default ShopPastJobs;
